import React, { useState, useEffect } from 'react';
import './App.css';
import { auth, db } from './firebase';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged, 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [apiKey, setApiKey] = useState<string>('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setLoading(true);
      setUser(u);
      if (u) {
        try {
          const userDocRef = doc(db, 'users', u.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists() && userDoc.data().apiKey) {
            setApiKey(userDoc.data().apiKey);
          } else {
            console.log("Generating new API key for user...");
            const newKey = 'ik_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            
            // 1. Create the user record
            await setDoc(userDocRef, {
              apiKey: newKey,
              tier: 'free',
              usage: 0,
              email: u.email
            }, { merge: true });

            // 2. Create the reverse lookup record for the Worker
            await setDoc(doc(db, 'api_keys', newKey), {
              userId: u.uid,
              tier: 'free'
            });

            setApiKey(newKey);
          }
        } catch (err: any) {
          console.error("Firestore Error:", err);
          setError(`Firebase Error: ${err.code || err.message}`);
        }
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const googleLogin = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider).catch(err => setError(err.message));
  };

  const emailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading && !user) return <div className="container">Loading...</div>;

  return (
    <div className="container">
      <nav>
        <a href="/" className="logo">IKOKI</a>
        {user ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span>{user.email}</span>
            <button className="btn" onClick={() => signOut(auth)}>Logout</button>
          </div>
        ) : (
          <button className="btn" onClick={() => (document.getElementById('auth-form') as HTMLElement).scrollIntoView()}>Login</button>
        )}
      </nav>

      {!user ? (
        <>
          <section className="hero">
            <h1>High-Volume PDF & HTML Extraction</h1>
            <p>Zero infrastructure cost. Lightning fast. Built for AI agents and RAG pipelines.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
               <button className="btn" onClick={googleLogin} style={{ fontSize: '1.1rem' }}>Get Started with Google</button>
            </div>
          </section>

          <section id="auth-form" className="card" style={{ maxWidth: '400px', margin: '40px auto' }}>
            <h3>{isLogin ? 'Login' : 'Sign Up'}</h3>
            <form onSubmit={emailAuth} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <input 
                type="email" 
                placeholder="Email" 
                className="input" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                required 
              />
              <input 
                type="password" 
                placeholder="Password" 
                className="input" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                required 
              />
              {error && <p style={{ color: 'red', fontSize: '0.8rem' }}>{error}</p>}
              <button type="submit" className="btn">{isLogin ? 'Login' : 'Sign Up'}</button>
            </form>
            <p style={{ marginTop: '20px', textAlign: 'center', fontSize: '0.9rem' }}>
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button onClick={() => setIsLogin(!isLogin)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 'bold' }}>
                {isLogin ? 'Sign Up' : 'Login'}
              </button>
            </p>
          </section>
          
          <section className="pricing">
            <div className="price-card">
              <h3>Free</h3>
              <h2>$0</h2>
              <p>1,000 requests / mo</p>
              <button className="btn" style={{ background: '#e2e8f0', color: '#1e293b' }}>Default</button>
            </div>
            <div className="price-card featured">
              <h3>Pro</h3>
              <h2>$29<span>/mo</span></h2>
              <p>250,000 requests / mo</p>
              <button className="btn">Upgrade</button>
            </div>
          </section>
        </>
      ) : (
        <section style={{ marginTop: '60px' }}>
          <h2>Your Dashboard</h2>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          <div className="card">
            <h3>API Key</h3>
            <div className="code-snippet">
              <code>{apiKey || 'Generating...'}</code>
            </div>
            <p style={{ marginTop: '20px', color: 'var(--gray)' }}>Keep this key secret. Use it in the <code>x-api-key</code> header.</p>
          </div>

          <div className="card" style={{ marginTop: '40px' }}>
            <h3>Quick Start</h3>
            <p>Convert HTML to Markdown:</p>
            <div className="code-snippet">
              <pre>
{`curl -X POST https://html-parser.your-subdomain.workers.dev \\
  -H "x-api-key: ${apiKey}" \\
  -d '{"url": "https://example.com"}'`}
              </pre>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default App;
